// THE BULWARK CUBE, "the Home Guard": a regiment of 36 drafted cast-iron stoves marching as a hollow
// cube (Godot scripts/enemies/bulwark_cube.gd + golden_bulwark.gd, docs/BOSS_GRAMMAR.md §5-6).
// Server-only brain; the 36 soldiers are ordinary enemies (EType.Bulwark) so grenades, rockets,
// melee, specials, prime/detonate, burn and lag compensation all reach them.
//
// Read surface: the formation. Core verb: positioning. Windows come from formation breaks.
//   Every soldier faces the posse with a riot-door SLAB taller than he is (an oven door off its
//   hinges, AP5: shots clang off) held to his shield side, and his own FIREBOX DOOR (AP5 too) shut
//   over the gun side of his belly. Bodies are bare from ABOVE (the stovepipe FLUE is a crit), from
//   BEHIND, from the flank, over the firebox door (the dome), and whenever the regiment rests.
//   The beat (G1-G3 at army altitude): ENGAGE a formation → PARADE REST (doors swung open on their
//   hinges, the stove fronts bare: ground fire finally lands) → DRILL CALL (1 s horn + stamp + nameplate: the
//   tell for the next formation) → ENGAGE. Escalation shortens the rest, never removes it.
//   Formations:
//     ADVANCE       the cube marches at its target, strongest wall turned to the front; the front
//                   wall's sidearms fire. Flank it while it turns, or fly over it.
//     ENCIRCLE      the regiment rings its target, slabs in, firing inward: fly out, or let the rest
//                   of the posse shoot the soldiers' bare backs from outside the ring.
//     GRINDER       the cube spins close: slab bashes, no fire.
//     GAUNTLET      two corridor walls charge down the target's lane: get out of the lane.
//     CEILING DROP  the top row vaults 13 m and crashes onto rings locked at the tell (1.25 s,
//                   the super; one hit per cowboy per drop). Landed droppers sit planted, bare.
//   SYNC SLAM overlays any formation every ~13 s: 0.9 s crouch, then a shockwave ring rolls out
//   (jump it), then a short planted rest.
// Kit parity (§2): the BANNERMAN (one at a time) carries a gold pilot light on his back (AV0 ×1.25)
//   and a weaker AV2 slab; when he falls the banner passes to the next in line. Fire is answered
//   in-fiction: a burning soldier breaks ranks to stop-drop-and-roll, leaving a hole (and his body
//   bare) while he rolls. Down to 9, the cube breaks and the survivors fight on their own.

import { DT, clamp, len2, lerp, lerpAngle, secs, wrapAngle } from '../core';
import type { Reader, Writer } from '../codec';
import { Ev } from '../codec';
import { EType } from '../defs';
import { BKind, BULLETS } from '../bullets';
import { EState, Enemy, historyAt, stepStatus } from '../enemies';
import { PlayerState, isTargetable } from '../player';
import { NO_GROUND } from '../world';
import type { Game } from '../game';
import { BossBase, BossBeat, BossDef, BossHit, BossKind, BossNet, Ray } from './types';

// ------------------------------------------------------------------ the regiment
export const WALLS = 4, ROWS = 3, COLS = 3, UNITS = WALLS * ROWS * COLS;
export const CUBE_HALF = 2.7, COL_SP = 1.75;
/** each level up a wall's columns close in by this fraction (a tapering phalanx, ref_03) */
export const COL_TAPER = 0.2;
/** ...and the middle column of each wall stands this much further out per level, so the doors shingle */
export const COL_STAGGER = 0.14;
/** The stack (Godot's bulwark_cube.gd stacks its three rows too, row_height 2.1 on ~2.2 m stoves).
 *  Here the rows step BACK like ref_03's phalanx: each soldier stands on the back of the brim of
 *  the one below (ROW_H) and STACK_FWD behind him (negative = back), so every visor in the wall
 *  shows under the slab of the soldier above, and the lower one's elbowed stovepipe tucks under
 *  the upper one's doors. */
export const ROW_H = 2.15, STACK_FWD = -0.6;
/** a soldier's hull, solo; scaled per posse member and run tier at spawn */
export const UNIT_HP = 70;
const LEASH = 34; // slots never leave this circle around the arena centre
const STANDOFF = 9;
const MARCH = 2.6; // m/s, the cube centre
const WALK = 4.5; // m/s, a soldier catching up with its slot
const TURN = 2.2; // rad/s, a soldier turning its slab
const CUBE_TURN = 1.8; // rad/s, the cube presenting its strongest wall
const BERSERK = 9;

export const enum Fm { None = 0, Advance = 1, Encircle = 2, Grinder = 3, Gauntlet = 4, Drop = 5 }
/** attack id of the sync slam (Ev.Boss Attack a=6) */
export const SLAM = 6;
export const FORMATION_NAMES = ['', 'ADVANCE', 'ENCIRCLE', 'GRINDER', 'GAUNTLET', 'CEILING DROP', 'SYNC SLAM'];
/** army state */
export const enum Cs { Engaged = 0, Parade = 1, Drill = 2 }
/** net.phase bits */
export const enum CubeBit { StateMask = 3, Released = 4, SlamTell = 8, Ring = 16, Intro = 32 }
export const enum DropSt { None = 0, Tell = 1, Rise = 2, Hang = 3, Fall = 4, Recover = 5 }
/** this boss's own Ev.Boss beats */
export const enum CubeEv { Banner = 16, Release = 17, Landed = 18, Slam = 19, Parade = 20 }

const DROP_SECS = [0, 1.25, 0.5, 0.35, 0.32, 1.1];
const DROP_APEX = 13, DROP_R = 3.4, DROP_DMG = 26, DROP_CLUSTER = 2.6, DROP_MAX = 6;
const SLAM_TELL = 0.9, SLAM_EXEC = 0.8, SLAM_R0 = CUBE_HALF + 1, SLAM_R1 = 13, SLAM_DMG = 22, SLAM_REST = 1.3;
const BASH_DMG = 8, BASH_GATE = secs(0.8);
const MUZZLE = [-0.8, 1.2, 1.06];

// ---- part volumes, in a soldier's model space (metres, feet at 0, the slab faces +Z):
// Assets/bosses/bulwark_sergeant/bulwark_sergeant.glb nodes body / shield_slab / head / fx_eyes (visor + flue),
// and bulwark_bannerman.glb wp_banner_core. The renderer draws the same GLBs at scale 1.
type Box = readonly [number, number, number, number, number, number]; // x0 y0 z0 x1 y1 z1
/** The riot door is ONE flat round-topped slab held to the shield-arm side (+x), from just off the
 *  centre line out past the shoulder (SLAB; the box keeps the arch's square corners). The gun side
 *  is covered by the stove's own firebox door, a flat plate on the belly (APRON), up to the chin:
 *  together they close the whole front from 0.22 m up to the shoulder, as the old slab did, and
 *  the dome and visor show over the firebox door, past the slab's inner edge. */
export const SLAB: Box = [0.03, 0.22, 0.67, 1.08, 2.44, 0.92];
export const APRON: Box = [-0.66, 0.22, 0.63, 0.08, 1.64, 0.76];
/** Planted (parade rest, landed droppers) both doors swing OPEN on their outer hinges (model x, z)
 *  (+ = turns like three.js rotation.y): the slab's free edge and the firebox door's free edge
 *  both come forward, and the stove front shows. The client swings shield_slab and firebox_door
 *  about the same hinges. */
export const SLAB_HINGE = [1.05, 0.76] as const, SLAB_SWING = 1.75;
export const APRON_HINGE = [-0.635, 0.66] as const, APRON_SWING = -1.4;
function swung(b: Box, hinge: readonly [number, number], ang: number): Box {
  const c = Math.cos(ang), s = Math.sin(ang);
  let x0 = 1e9, z0 = 1e9, x1 = -1e9, z1 = -1e9;
  for (const x of [b[0], b[3]]) for (const z of [b[2], b[5]]) {
    const dx = x - hinge[0], dz = z - hinge[1];
    const rx = hinge[0] + dx * c + dz * s, rz = hinge[1] - dx * s + dz * c;
    x0 = Math.min(x0, rx); x1 = Math.max(x1, rx); z0 = Math.min(z0, rz); z1 = Math.max(z1, rz);
  }
  return [x0, b[1], z0, x1, b[4], z1];
}
export const SLAB_OPEN = swung(SLAB, SLAB_HINGE, SLAB_SWING), APRON_OPEN = swung(APRON, APRON_HINGE, APRON_SWING);
export const BODY: Box = [-0.64, 0, -0.58, 0.64, 1.64, 0.58];
/** the elbowed stovepipe (it bends to either side) and the lid crown under it */
export const FLUE: Box = [-0.44, 2.02, -0.2, 0.44, 2.56, 0.4];
export const HEAD: Box = [-0.52, 1.63, -0.46, 0.52, 2.22, 0.54];
export const CORE: Box = [-0.12, 0.72, -0.66, 0.12, 1.0, -0.52];

/** which part a shot found */
export const enum CPart { None = 0, Slab = 1, Body = 2, Head = 3, Flue = 4, Core = 5 }
export const PART_ARMOR: { av: number; mul: number; weak: boolean }[] = [
  { av: 0, mul: 1, weak: false },
  { av: 4, mul: 0.25, weak: false }, // the riot-door slab: AP5, everything clangs
  { av: 0, mul: 1, weak: false }, // the stove belly + firebox, bare from above / behind / the flank / at rest
  { av: 2, mul: 1, weak: false }, // the domed lid + visor drum standing over the slab
  { av: 0, mul: 1.25, weak: true }, // the flue (stovepipe + lid crown), seen from above
  { av: 0, mul: 1.25, weak: true }, // the bannerman's gold pilot light
];

export interface CubeState extends BossBase {
  kind: BossKind.BulwarkCube;
  max: number; alive: number;
  homeX: number; homeZ: number;
  cx: number; cz: number; yaw: number; front: number; frontT: number;
  form: Fm; next: Fm; st: Cs; stT: number; stDur: number; formT: number;
  target: number; tx: number; tz: number;
  ax: number; az: number; // encircle anchor
  laneX: number; laneZ: number; // gauntlet lane
  slamCd: number; slamT: number; ringT: number; restT: number; hitMask: number;
  drop: DropSt; dropT: number; dropGap: number; droppers: number[];
  fromX: Float32Array; fromY: Float32Array; fromZ: Float32Array; landX: Float32Array; landZ: Float32Array;
  clusters: { x: number; z: number }[];
  bearer: number;
  released: boolean;
  introDone: boolean;
  fireT: number; fireRR: number;
  roll: Int16Array; rollX: Float32Array; rollZ: Float32Array;
  rest: Int16Array; shotT: Int16Array; // released soldiers: own rest + fire clocks
  bashAt: Int32Array; // last bash tick per pid
  units: (Enemy | null)[]; // by slot index (e.mode), refreshed every tick
  stack: Int8Array;
}

export interface CubeNet extends BossNet {
  kind: BossKind.BulwarkCube;
  /** eid = the bannerman; atk = the formation; phase = CubeBit flags */
  /** the cube's centre (the slam ring rolls out from here) */
  cx: number; cz: number;
  next: number; alive: number; max: number;
  /** 0..1 through the current rest / call / slam tell */
  prog: number;
  /** the slam ring's radius now (m), 0 = none */
  ring: number;
  drop: DropSt; dropK: number;
  clusters: { x: number; z: number }[];
}

const frenzy = (s: CubeState) => clamp(1 - s.alive / Math.max(1, s.max), 0, 1);
const slotOf = (e: Enemy) => e.mode;
const wallOf = (i: number) => Math.floor(i / 9);
const rowOf = (i: number) => Math.floor((i % 9) / 3);
const colOf = (i: number) => i % 3;
// wall normals / tangents in cube space (x right, z back: wall 0 is the front, local -Z)
const WN = [[0, -1], [1, 0], [0, 1], [-1, 0]];
const WT = [[1, 0], [0, 1], [-1, 0], [0, -1]];

function groundAt(g: Game, x: number, z: number, y: number) {
  const t = g.world.terrain(x, z);
  return t === NO_GROUND ? y : t;
}

function targets(g: Game) {
  const out: PlayerState[] = [];
  for (const p of g.players.values()) if (isTargetable(p) && p.phase === 0) out.push(p);
  return out;
}

// ------------------------------------------------------------------ geometry (shared with the client)
/** cube-space (lx, lz) → world offset, for cube yaw `yaw` (forward = (-sin yaw, -cos yaw)) */
export function cubeRot(yaw: number, lx: number, lz: number, out: { x: number; z: number }) {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  out.x = lx * c + lz * s; out.z = -lx * s + lz * c;
  return out;
}

/** Ray against one soldier at (x, y, z, yaw). Planted = slab down; rolling = no slab; bearer = AV2 slab + core. */
export function unitRaycast(
  x: number, y: number, z: number, yaw: number, planted: boolean, rolling: boolean, bearer: boolean,
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number,
  out: { t: number; part: CPart },
) {
  out.t = -1; out.part = CPart.None;
  // broad phase: a sphere round the whole soldier
  {
    const cx = x - ox, cy = y + 1.25 - oy, cz = z - oz;
    const b = cx * dx + cy * dy + cz * dz;
    const c = cx * cx + cy * cy + cz * cz - 1.8 * 1.8;
    if (c > 0 && (b < 0 || b * b < c)) return out;
  }
  // into the soldier's model frame (model +Z = its forward; group yaw = yaw + π like every boss)
  const c = Math.cos(yaw + Math.PI), s = Math.sin(yaw + Math.PI);
  const px = ox - x, pz = oz - z;
  const lox = c * px - s * pz, loz = s * px + c * pz, loy = oy - y;
  const ldx = c * dx - s * dz, ldz = s * dx + c * dz, ldy = dy;
  let best = maxT;
  const test = (bx: Box, part: CPart, bias: number, lift = 0) => {
    const t = rayBox(lox, loy, loz, ldx, ldy, ldz, bx[0], bx[1] - lift, bx[2], bx[3], bx[4] - lift, bx[5]);
    if (t >= 0 && t - bias < best) { best = t - bias; out.t = t; out.part = part; }
  };
  if (bearer) test(CORE, CPart.Core, 0.08);
  test(FLUE, CPart.Flue, 0.04);
  if (!rolling) { test(planted ? SLAB_OPEN : SLAB, CPart.Slab, 0); test(planted ? APRON_OPEN : APRON, CPart.Slab, 0); }
  test(HEAD, CPart.Head, 0);
  test(BODY, CPart.Body, 0);
  return out;
}

function rayBox(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  let tmin = 0, tmax = 1e9;
  const ax = [ox, oy, oz], ad = [dx, dy, dz], lo = [x0, Math.max(0, y0), z0], hi = [x1, y1, z1];
  for (let k = 0; k < 3; k++) {
    if (Math.abs(ad[k]) < 1e-9) { if (ax[k] < lo[k] || ax[k] > hi[k]) return -1; continue; }
    let t1 = (lo[k] - ax[k]) / ad[k], t2 = (hi[k] - ax[k]) / ad[k];
    if (t1 > t2) { const q = t1; t1 = t2; t2 = q; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  return tmin;
}

// ------------------------------------------------------------------ the def
const hp = { x: 0, y: 0, z: 0, yaw: 0 };
const uh = { t: -1, part: CPart.None };
const o2 = { x: 0, z: 0 };

export const BULWARK_CUBE: BossDef<CubeState> = {
  kind: BossKind.BulwarkCube,
  name: 'THE BULWARK CUBE',
  recall: 'RECALL NOTICE: THE BULWARK CUBE. 36 POT-BELLY STOVES, ONE DRILL SERGEANT, NO GAPS. CRACK THE FORMATION.',
  types: [EType.Bulwark],
  /** units are robots: gun elements proc on them */
  procs: true,

  spawn(g, x, z, lookX, lookZ, n, tierMul) {
    const cx = x + 6, cz = z + 4;
    const yaw = Math.atan2(-(lookX - cx), -(lookZ - cz));
    const s: CubeState = {
      kind: BossKind.BulwarkCube, eid: 0, max: UNITS, alive: UNITS,
      homeX: x, homeZ: z, cx, cz, yaw, front: 0, frontT: 0,
      form: Fm.None, next: Fm.Advance, st: Cs.Parade, stT: 0, stDur: 1, formT: 0,
      target: -1, tx: lookX, tz: lookZ, ax: cx, az: cz, laneX: 0, laneZ: -1,
      slamCd: secs(10), slamT: 0, ringT: 0, restT: 0, hitMask: 0,
      drop: DropSt.None, dropT: 0, dropGap: 0, droppers: [],
      fromX: new Float32Array(DROP_MAX), fromY: new Float32Array(DROP_MAX), fromZ: new Float32Array(DROP_MAX),
      landX: new Float32Array(DROP_MAX), landZ: new Float32Array(DROP_MAX), clusters: [],
      bearer: 0, released: false, introDone: false, fireT: secs(1), fireRR: 0,
      roll: new Int16Array(UNITS), rollX: new Float32Array(UNITS), rollZ: new Float32Array(UNITS),
      rest: new Int16Array(UNITS), shotT: new Int16Array(UNITS), bashAt: new Int32Array(32).fill(-9999),
      units: new Array(UNITS).fill(null), stack: new Int8Array(UNITS),
    };
    const hpEach = UNIT_HP * (1 + 0.35 * (n - 1)) * tierMul;
    for (let i = 0; i < UNITS; i++) {
      advanceSlot(s, i, o2);
      const e = g.spawnEnemy(EType.Bulwark, o2.x, o2.z);
      e.mode = i;
      e.maxHp = e.hp = hpEach;
      e.yaw = yaw;
      e.x -= Math.sin(yaw) * rowOf(i) * STACK_FWD; e.z -= Math.cos(yaw) * rowOf(i) * STACK_FWD;
      e.y = groundAt(g, e.x, e.z, e.y) + rowOf(i) * ROW_H;
      // they rain in a column at a time, the top rows last
      e.stDur = secs(1.6 + (wallOf(i) * 3 + colOf(i)) * 0.04 + rowOf(i) * 0.35);
      for (let k = 0; k < e.hx.length; k++) { e.hx[k] = e.x; e.hy[k] = e.y; e.hz[k] = e.z; e.hyaw[k] = e.yaw; }
      s.units[i] = e;
    }
    // the first bannerman: the top of the front wall, centre file
    s.bearer = s.units[2 * 3 + 1]!.id;
    return s;
  },

  step(g, s) {
    stepCube(g, s);
  },

  raycast(g, s, r: Ray, view, maxT, out: BossHit) {
    out.t = -1; out.e = null;
    let best = maxT;
    const planted = isPlanted(s);
    for (const e of g.enemies) {
      if (e.dead || e.type !== EType.Bulwark || e.st === EState.Spawn) continue;
      historyAt(e, view, g.tick, hp);
      const i = slotOf(e);
      const pl = planted || e.st === EState.Recover;
      unitRaycast(hp.x, hp.y, hp.z, hp.yaw, pl, s.roll[i] > 0, e.id === s.bearer, r.ox, r.oy, r.oz, r.dx, r.dy, r.dz, best, uh);
      if (uh.t < 0) continue;
      // a bannerman's slab is only AV2 (the any-kit frontal answer)
      const pa = PART_ARMOR[uh.part];
      best = uh.t;
      out.t = uh.t; out.e = e; out.part = uh.part; out.idx = i;
      out.av = uh.part === CPart.Slab && e.id === s.bearer ? 2 : pa.av;
      out.mul = uh.part === CPart.Slab && e.id === s.bearer ? 0.8 : pa.mul;
      out.weak = pa.weak; out.head = pa.weak;
    }
    return out;
  },

  tookHit() { /* the soldiers' hulls are their own pools: nothing to forward */ },

  died(g, s, e) {
    s.units[slotOf(e)] = null;
    let left = 0;
    for (const o of g.enemies) if (!o.dead && o.type === EType.Bulwark && o !== e) left++;
    s.alive = left;
    if (e.id === s.bearer && left > 0) passBanner(g, s, e);
    return left === 0;
  },

  note(_s, kind) {
    if (kind === CubeEv.Banner) return 'THE BANNERMAN IS DOWN. THE BANNER PASSES DOWN THE LINE: FIND THE GOLD PILOT LIGHT.';
    if (kind === CubeEv.Release) return 'THE CUBE IS BROKEN. THE HOME GUARD FIGHTS ON ALONE.';
    return undefined;
  },

  enc(w: Writer, _g, s) {
    w.u16w(s.bearer); w.cmw(s.cx); w.cmw(s.cz); w.u8w(s.form); w.u8w(s.next);
    const bits = cubeState(s) | (s.released ? CubeBit.Released : 0) | (s.slamT > 0 ? CubeBit.SlamTell : 0) | (s.ringT > 0 ? CubeBit.Ring : 0) | (s.introDone ? 0 : CubeBit.Intro);
    w.u8w(bits); w.u8w(s.alive); w.u8w(s.max);
    w.u8w(Math.round(clamp(stateProg(s), 0, 1) * 255));
    w.u8w(s.ringT > 0 ? Math.round(ringRadius(s) * 10) : 0);
    w.u8w(s.drop);
    w.u8w(s.drop ? Math.round(clamp(1 - s.dropT / secs(DROP_SECS[s.drop]), 0, 1) * 255) : 0);
    const n = s.drop && s.drop <= DropSt.Fall ? Math.min(4, s.clusters.length) : 0;
    w.u8w(n);
    for (let i = 0; i < n; i++) { w.cmw(s.clusters[i].x); w.cmw(s.clusters[i].z); }
  },

  dec(r: Reader): CubeNet {
    const eid = r.u16(), cx = r.cm(), cz = r.cm(), atk = r.u8(), next = r.u8(), phase = r.u8(), alive = r.u8(), max = r.u8();
    const prog = r.u8() / 255, ring = r.u8() / 10, drop = r.u8() as DropSt, dropK = r.u8() / 255;
    const n = r.u8();
    const clusters: { x: number; z: number }[] = [];
    for (let i = 0; i < n; i++) clusters.push({ x: r.cm(), z: r.cm() });
    return { kind: BossKind.BulwarkCube, eid, cx, cz, atk, phase, next, alive, max, prog, ring, drop, dropK, clusters };
  },
};

// ------------------------------------------------------------------ state helpers
function cubeState(s: CubeState): Cs {
  return s.restT > 0 ? Cs.Parade : s.st;
}
function stateProg(s: CubeState) {
  if (s.slamT > 0) return 1 - s.slamT / secs(SLAM_TELL);
  if (s.restT > 0) return 1 - s.restT / secs(SLAM_REST);
  return s.st === Cs.Engaged ? 0 : 1 - s.stT / Math.max(1, s.stDur);
}
function ringRadius(s: CubeState) {
  return lerp(SLAM_R0, SLAM_R1, 1 - s.ringT / secs(SLAM_EXEC));
}
/** whole regiment planted: slabs down, bodies bare */
export function isPlanted(s: CubeState) {
  return s.restT > 0 || s.st === Cs.Parade || !s.introDone;
}

/** ADVANCE / GRINDER / DROP slot: the hollow cube around (cx, cz) at the cube yaw. */
function advanceSlot(s: CubeState, i: number, out: { x: number; z: number }) {
  const w = wallOf(i), c = colOf(i), sp = COL_SP * (1 - COL_TAPER * s.stack[i]);
  const rad = CUBE_HALF + (c === 1 ? COL_STAGGER * s.stack[i] : 0);
  const lx = WN[w][0] * rad + WT[w][0] * (c - 1) * sp;
  const lz = WN[w][1] * rad + WT[w][1] * (c - 1) * sp;
  cubeRot(s.yaw, lx, lz, out);
  out.x += s.cx; out.z += s.cz;
  return out;
}

function slotFor(s: CubeState, i: number, out: { x: number; z: number }) {
  if (s.released) return out;
  switch (s.form) {
    case Fm.Encircle: {
      const a = (Math.PI * 2 * i) / UNITS + s.yaw * 0.25;
      out.x = s.ax + Math.cos(a) * 9; out.z = s.az + Math.sin(a) * 9;
      return out;
    }
    case Fm.Gauntlet: {
      const side = i < 18 ? -1 : 1, depth = Math.floor((i % 18) / 3);
      const px = -s.laneZ, pz = s.laneX;
      out.x = s.cx + s.laneX * depth * 2.4 + px * side * 2.8;
      out.z = s.cz + s.laneZ * depth * 2.4 + pz * side * 2.8;
      return out;
    }
    default: return advanceSlot(s, i, out);
  }
}

/** Stack each column bottom-up so survivors drop into dead supports' places. */
function computeStacks(s: CubeState) {
  const encircle = s.form === Fm.Encircle && !s.released;
  // column key → count so far, walking rows bottom-up
  const seen = new Int8Array(24);
  for (let row = 0; row < ROWS; row++) {
    for (let i = 0; i < UNITS; i++) {
      const e = s.units[i];
      if (!e) continue;
      const gauntlet = s.form === Fm.Gauntlet;
      const r = gauntlet ? (i % 18) % 3 : rowOf(i);
      if (r !== row) continue;
      if (encircle || s.released || s.roll[i] > 0) { s.stack[i] = 0; continue; }
      const key = gauntlet ? (i < 18 ? 0 : 12) + Math.floor((i % 18) / 3) : wallOf(i) * 3 + colOf(i);
      s.stack[i] = seen[key]++;
    }
  }
}

function strongestWall(s: CubeState) {
  const n = [0, 0, 0, 0];
  for (let i = 0; i < UNITS; i++) if (s.units[i]) n[wallOf(i)]++;
  let best = s.front;
  for (let w = 0; w < 4; w++) if (n[w] > n[best]) best = w;
  return best;
}

function retarget(g: Game, s: CubeState) {
  let best = -1, bd = 1e9;
  for (const p of targets(g)) {
    const d = len2(p.x - s.cx, p.z - s.cz) + g.rng.range(0, 10);
    if (d < bd) { bd = d; best = p.id; }
  }
  s.target = best;
}

function passBanner(g: Game, s: CubeState, dead: Enemy) {
  // the next in line: the highest soldier still standing, nearest the cube's front
  let best: Enemy | null = null, score = -1e9;
  for (const o of g.enemies) {
    if (o.dead || o === dead || o.type !== EType.Bulwark) continue;
    const i = slotOf(o);
    const sc = s.stack[i] * 10 + (wallOf(i) === s.front ? 5 : 0) - len2(o.x - dead.x, o.z - dead.z) * 0.1;
    if (sc > score) { score = sc; best = o; }
  }
  if (!best) return;
  s.bearer = best.id;
  // G5: the banner flies to him (the client draws the flight from the old bearer to the new)
  g.emit(Ev.Boss, [best.id, CubeEv.Banner, slotOf(dead), slotOf(best)]);
  g.emit(Ev.Notice, [], BULWARK_CUBE.note!(s, CubeEv.Banner, 0, 0)!);
}

function setUnit(e: Enemy, st: EState, dur: number) {
  if (e.st !== st) { e.st = st; e.stT = 0; e.stDur = dur; }
}

// ------------------------------------------------------------------ the brain
function stepCube(g: Game, s: CubeState) {
  // refresh the roster (slot index rides e.mode)
  s.units.fill(null);
  let alive = 0;
  for (const e of g.enemies) if (!e.dead && e.type === EType.Bulwark) { s.units[slotOf(e)] = e; alive++; }
  s.alive = alive;
  if (alive === 0) return;

  // status timers, burn, prime expiry; staggers from detonations hold a soldier still
  for (let i = 0; i < UNITS; i++) {
    const e = s.units[i];
    if (!e) continue;
    e.stT++;
    if (!stepStatus(e, g)) { s.units[i] = null; continue; }
    if (e.stunT > 0 && e.stunT < secs(1) && e.st !== EState.Stun) e.stunT = 0; // no micro-flinch in ranks
    if (s.roll[i] > 0 && --s.roll[i] === 0) e.burnT = 0; // rolled it out
  }

  // ---- the drop-in
  if (!s.introDone) {
    let landed = 0;
    for (const e of s.units) {
      if (!e) continue;
      if (e.st === EState.Spawn && e.stT >= e.stDur) setUnit(e, EState.Recover, secs(0.5));
      if (e.st !== EState.Spawn) landed++;
    }
    if (landed === alive) {
      s.introDone = true;
      g.emit(Ev.Boss, [0, BossBeat.Intro, 1, 0]);
      retarget(g, s);
      startDrill(g, s, Fm.Advance);
    }
    return;
  }

  // ---- the cube breaks
  if (!s.released && alive <= BERSERK) {
    s.released = true; s.drop = DropSt.None; s.slamT = s.ringT = s.restT = 0;
    for (let i = 0; i < UNITS; i++) { s.rest[i] = secs(2 + (i % 5) * 0.6); s.shotT[i] = secs(1 + (i % 7) * 0.3); }
    g.emit(Ev.Boss, [0, CubeEv.Release, 0, 0]);
    g.emit(Ev.Notice, [], BULWARK_CUBE.note!(s, CubeEv.Release, 0, 0)!);
  }

  // ---- target (clamped to the leash: the cube never chases the posse off its island)
  const tp = s.target >= 0 ? g.players.get(s.target) : undefined;
  if (!tp || !isTargetable(tp) || tp.phase !== 0) retarget(g, s);
  const t = s.target >= 0 ? g.players.get(s.target) : undefined;
  if (t) {
    let ox = t.x - s.homeX, oz = t.z - s.homeZ;
    const l = len2(ox, oz);
    if (l > LEASH) { ox *= LEASH / l; oz *= LEASH / l; }
    s.tx = s.homeX + ox; s.tz = s.homeZ + oz;
  }

  if (s.released) { stepReleased(g, s); bash(g, s); return; }

  const fz = frenzy(s);
  // ---- the beat: PARADE → DRILL → ENGAGE
  let engaged = false;
  if (s.st === Cs.Parade) {
    if (--s.stT <= 0) startDrill(g, s, pickNext(g, s));
  } else if (s.st === Cs.Drill) {
    if (--s.stT <= 0) engage(g, s);
  } else {
    engaged = true;
    s.formT--;
    const busy = s.drop !== DropSt.None || s.slamT > 0 || s.ringT > 0 || s.restT > 0;
    if (s.formT <= 0 && !busy) {
      s.st = Cs.Parade; s.stDur = s.stT = secs(lerp(2.5, 1.2, fz));
      g.emit(Ev.Boss, [0, CubeEv.Parade, s.form, 0]);
      engaged = false;
    }
  }

  // ---- the sync slam overlays any formation but the drop
  const slamming = stepSlam(g, s, engaged);
  const resting = s.restT > 0;
  if (resting) s.restT--;

  // ---- formation motion
  if (engaged && !resting && s.slamT <= 0) moveFormation(s, fz);
  if (engaged && s.form === Fm.Drop && !resting) stepDrop(g, s);
  computeStacks(s);

  // ---- every soldier to his slot
  const planted = !engaged || resting;
  const tell = s.slamT > 0;
  const bob = engaged && s.form === Fm.Advance && !resting && !tell ? Math.abs(Math.sin(g.tick * DT * 3)) * 0.22 : 0;
  for (let i = 0; i < UNITS; i++) {
    const e = s.units[i];
    if (!e) continue;
    if (isDropper(s, i)) continue; // the drop drives its own arcs
    if (s.roll[i] > 0) { rollUnit(g, s, e, i); continue; }
    if (e.burnT > 0 && !planted) { startRoll(s, e, i); continue; }
    if (e.stunT > 0) { e.stunT--; setUnit(e, EState.Stun, e.stunT); continue; }
    slotFor(s, i, o2);
    // stacked soldiers stand on the lid of the one below: a step forward per level
    if (s.stack[i]) { const k = s.stack[i] * STACK_FWD; o2.x -= Math.sin(e.yaw) * k; o2.z -= Math.cos(e.yaw) * k; }
    if (!planted && !tell) walkTo(g, e, o2.x, o2.z, WALK);
    const gy = groundAt(g, e.x, e.z, e.y) + s.stack[i] * ROW_H + bob;
    e.y = e.y > gy ? Math.max(gy, e.y - 9 * DT) : Math.min(gy, e.y + 7 * DT);
    if (!planted) faceTo(e, s.tx, s.tz, TURN);
    setUnit(e, planted ? EState.Recover : s.st === Cs.Drill || tell ? EState.Windup : EState.Chase, planted ? s.stDur : 0);
  }
  if (s.st === Cs.Drill) for (const e of s.units) if (e && !e.stunT && s.roll[slotOf(e)] === 0) setUnit(e, EState.Windup, s.stDur);

  // ---- sidearms
  if (engaged && !resting && !slamming && s.form !== Fm.Grinder) fire(g, s, fz);
  if (engaged && !resting) bash(g, s);
}

function pickNext(g: Game, s: CubeState): Fm {
  const fz = frenzy(s);
  const pool: Fm[] = [Fm.Advance, Fm.Encircle, Fm.Grinder];
  if (fz >= 0.2) pool.push(Fm.Gauntlet);
  if (fz >= 0.33) pool.push(Fm.Drop, Fm.Drop);
  const opts = pool.filter((f) => f !== s.form);
  return opts[g.rng.int(0, opts.length - 1)];
}

function startDrill(g: Game, s: CubeState, next: Fm) {
  s.next = next;
  s.st = Cs.Drill; s.stDur = s.stT = secs(1.0);
  retarget(g, s);
  // the drill call: horn + stamp + the formation's name on the boss bar (the TELL)
  g.emit(Ev.Boss, [0, BossBeat.Attack, next, 0]);
}

function engage(g: Game, s: CubeState) {
  const fz = frenzy(s);
  s.st = Cs.Engaged;
  s.form = s.next;
  s.formT = secs(s.form === Fm.Drop ? lerp(8, 6, fz) : lerp(9, 4.5, fz));
  if (s.form === Fm.Encircle) { s.ax = s.tx; s.az = s.tz; }
  if (s.form === Fm.Gauntlet) {
    const dx = s.tx - s.cx, dz = s.tz - s.cz, l = len2(dx, dz) || 1;
    s.laneX = dx / l; s.laneZ = dz / l;
  }
  if (s.form === Fm.Drop) { s.drop = DropSt.None; s.dropGap = secs(0.4); }
  void g;
}

function moveFormation(s: CubeState, fz: number) {
  const dx = s.tx - s.cx, dz = s.tz - s.cz, d = len2(dx, dz);
  const toward = (spd: number) => { if (d > 0.01) { s.cx += (dx / d) * spd * DT; s.cz += (dz / d) * spd * DT; } };
  const faceYaw = Math.atan2(-dx, -dz);
  switch (s.form) {
    case Fm.Encircle: {
      // the ring tracks its target slowly enough to break out of
      const ax = s.tx - s.ax, az = s.tz - s.az, al = len2(ax, az);
      if (al > 0.05) { const st = Math.min(al, 1.6 * DT); s.ax += (ax / al) * st; s.az += (az / al) * st; }
      s.yaw = wrapAngle(s.yaw + 0.25 * DT);
      break;
    }
    case Fm.Grinder:
      if (d > 5) toward(MARCH); else if (d < 3.5) toward(-MARCH);
      s.yaw = wrapAngle(s.yaw + (0.75 + 0.3 * fz) * DT);
      break;
    case Fm.Gauntlet:
      s.cx += s.laneX * 3.0 * DT; s.cz += s.laneZ * 3.0 * DT;
      break;
    default: // ADVANCE / DROP: march, present the strongest wall
      if (d > STANDOFF) toward(MARCH * (1 + 0.3 * fz));
      if (--s.frontT <= 0) { s.frontT = secs(1); s.front = strongestWall(s); }
      s.yaw = lerpAngle(s.yaw, faceYaw + s.front * Math.PI / 2, clamp(CUBE_TURN * DT, 0, 1));
  }
  // never leave the arena
  const hx = s.cx - s.homeX, hz = s.cz - s.homeZ, hl = len2(hx, hz);
  if (hl > LEASH - CUBE_HALF) { s.cx = s.homeX + hx / hl * (LEASH - CUBE_HALF); s.cz = s.homeZ + hz / hl * (LEASH - CUBE_HALF); }
}

function walkTo(g: Game, e: Enemy, x: number, z: number, speed: number) {
  const dx = x - e.x, dz = z - e.z, d = len2(dx, dz);
  const step = speed * (e.slowT > 0 ? 0.5 : 1) * DT;
  let nx = x, nz = z;
  if (d > step) { nx = e.x + (dx / d) * step; nz = e.z + (dz / d) * step; }
  if (g.world.terrain(nx, nz) === NO_GROUND) return;
  e.vx = (nx - e.x) / DT; e.vz = (nz - e.z) / DT;
  e.x = nx; e.z = nz;
}

function faceTo(e: Enemy, x: number, z: number, rate: number) {
  const want = Math.atan2(-(x - e.x), -(z - e.z));
  const d = wrapAngle(want - e.yaw);
  e.yaw = wrapAngle(e.yaw + clamp(d, -rate * DT, rate * DT));
}

// ---- stop, drop and roll: fire opens holes instead of deleting the regiment
function startRoll(s: CubeState, e: Enemy, i: number) {
  s.roll[i] = secs(2);
  const a = e.yaw + (i % 2 ? 1.2 : -1.2);
  s.rollX[i] = -Math.sin(a); s.rollZ[i] = -Math.cos(a);
  s.stack[i] = 0;
  setUnit(e, EState.Stun, secs(2));
}
function rollUnit(g: Game, s: CubeState, e: Enemy, i: number) {
  walkTo(g, e, e.x + s.rollX[i] * 3, e.z + s.rollZ[i] * 3, 4.2);
  const gy = groundAt(g, e.x, e.z, e.y);
  e.y = e.y > gy ? Math.max(gy, e.y - 9 * DT) : gy;
  setUnit(e, EState.Stun, secs(2));
}

// ---- the sync slam
function stepSlam(g: Game, s: CubeState, engaged: boolean): boolean {
  if (s.slamT > 0) {
    if (--s.slamT === 0) { s.ringT = secs(SLAM_EXEC); s.hitMask = 0; g.emit(Ev.Boss, [0, CubeEv.Slam, 0, 0]); }
    return true;
  }
  if (s.ringT > 0) {
    const r = ringRadius(s);
    for (const p of targets(g)) {
      const bit = 1 << (p.id & 31);
      if (s.hitMask & bit) continue;
      const d = len2(p.x - s.cx, p.z - s.cz);
      const above = p.y - groundAt(g, p.x, p.z, p.y);
      if (Math.abs(d - r) < 1.3 && above < 1.1) {
        s.hitMask |= bit;
        g.hurtPlayer(p, SLAM_DMG, s.cx, s.cz, 0, ((p.x - s.cx) / (d || 1)) * 14, ((p.z - s.cz) / (d || 1)) * 14);
      }
    }
    if (--s.ringT === 0) s.restT = secs(SLAM_REST); // planted: the slam's owed recovery
    return true;
  }
  if (!engaged || s.form === Fm.Drop || s.restT > 0) return false;
  if (--s.slamCd <= 0) {
    s.slamCd = secs(lerp(13, 8, frenzy(s)));
    s.slamT = secs(SLAM_TELL);
    g.emit(Ev.Boss, [0, BossBeat.Attack, SLAM, 0]);
    return true;
  }
  return false;
}

// ---- the ceiling drop
function isDropper(s: CubeState, i: number) {
  return s.drop !== DropSt.None && s.droppers.indexOf(i) >= 0;
}

function stepDrop(g: Game, s: CubeState) {
  if (s.drop === DropSt.None) {
    if (s.dropGap > 0) { s.dropGap--; return; }
    if (s.formT < secs(3.6)) return; // not enough time for a full cycle: the formation winds down
    beginDrop(g, s);
    return;
  }
  const dur = secs(DROP_SECS[s.drop]);
  s.dropT--;
  const k = 1 - s.dropT / dur;
  for (let n = 0; n < s.droppers.length; n++) {
    const e = s.units[s.droppers[n]];
    if (!e) continue;
    const lx = s.landX[n], lz = s.landZ[n];
    const ly = groundAt(g, lx, lz, s.fromY[n]);
    const apex = ly + DROP_APEX;
    switch (s.drop) {
      case DropSt.Tell: setUnit(e, EState.Windup, dur); faceTo(e, lx, lz, TURN * 2); break;
      case DropSt.Rise: {
        const q = 1 - (1 - k) * (1 - k);
        e.x = lerp(s.fromX[n], lx, q); e.z = lerp(s.fromZ[n], lz, q); e.y = lerp(s.fromY[n], apex, q);
        setUnit(e, EState.Attack, secs(DROP_SECS[2] + DROP_SECS[3] + DROP_SECS[4]));
        e.airborne = 1;
        break;
      }
      case DropSt.Hang: e.x = lx; e.z = lz; e.y = apex; break;
      case DropSt.Fall: e.x = lx; e.z = lz; e.y = lerp(apex, ly, k * k); break;
      case DropSt.Recover: setUnit(e, EState.Recover, dur); e.airborne = 0; break;
    }
  }
  if (s.dropT > 0) return;
  if (s.drop === DropSt.Fall) {
    // impact: one hit per cowboy per drop, whatever lands on them (G4)
    for (let n = 0; n < s.droppers.length; n++) {
      const e = s.units[s.droppers[n]];
      if (!e) continue;
      e.y = groundAt(g, e.x, e.z, e.y);
      for (const p of targets(g)) {
        const bit = 1 << (p.id & 31);
        if (s.hitMask & bit) continue;
        const dx = p.x - e.x, dz = p.z - e.z, d = len2(dx, dz);
        if (d > DROP_R || Math.abs(p.y - e.y) > 3) continue;
        s.hitMask |= bit;
        let kx = dx / (d || 1), kz = dz / (d || 1);
        // shoves near the rim push inboard, never off the island
        const hx = s.homeX - p.x, hz = s.homeZ - p.z;
        if (len2(hx, hz) > 26 && kx * hx + kz * hz < 0) { kx = -kx; kz = -kz; }
        g.hurtPlayer(p, DROP_DMG, e.x, e.z, 0, kx * 14, kz * 14);
      }
    }
    g.emit(Ev.Boss, [0, CubeEv.Landed, s.droppers.length, 0]);
  }
  if (s.drop === DropSt.Recover) {
    s.drop = DropSt.None; s.droppers = []; s.clusters = []; s.dropGap = secs(0.8);
    return;
  }
  s.drop++;
  s.dropT = secs(DROP_SECS[s.drop]);
}

function beginDrop(g: Game, s: CubeState) {
  // the droppers: the top of the stack (or the front wall once it's one row tall)
  let top = 0;
  for (let i = 0; i < UNITS; i++) if (s.units[i] && s.roll[i] === 0) top = Math.max(top, s.stack[i]);
  const pick: number[] = [];
  for (let i = 0; i < UNITS && pick.length < DROP_MAX; i++) {
    const e = s.units[i];
    if (!e || s.roll[i] > 0 || e.stunT > 0) continue;
    if (top > 0 ? s.stack[i] === top : wallOf(i) === s.front) pick.push(i);
  }
  const ts = targets(g).filter((p) => len2(p.x - s.cx, p.z - s.cz) < 48).slice(0, 4);
  if (!pick.length || !ts.length) { s.dropGap = secs(1); return; }
  s.clusters = ts.map((p) => {
    let ox = p.x - s.homeX, oz = p.z - s.homeZ;
    const l = len2(ox, oz);
    if (l > LEASH) { ox *= LEASH / l; oz *= LEASH / l; }
    return { x: s.homeX + ox, z: s.homeZ + oz };
  });
  s.droppers = pick;
  for (let n = 0; n < pick.length; n++) {
    const e = s.units[pick[n]]!;
    const c = s.clusters[n % s.clusters.length];
    const ring = n >= s.clusters.length ? DROP_CLUSTER : 0.6;
    const a = n * 2.39996;
    let lx = c.x + Math.cos(a) * ring, lz = c.z + Math.sin(a) * ring;
    if (g.world.terrain(lx, lz) === NO_GROUND) { lx = c.x; lz = c.z; }
    s.landX[n] = lx; s.landZ[n] = lz;
    s.fromX[n] = e.x; s.fromY[n] = e.y; s.fromZ[n] = e.z;
  }
  s.drop = DropSt.Tell; s.dropT = secs(DROP_SECS[DropSt.Tell]); s.hitMask = 0;
  g.emit(Ev.Boss, [0, BossBeat.Attack, Fm.Drop, 1]); // b = 1: a drop cycle's crouch, not a drill call
}

// ---- sidearms: a staggered volley of slow bolts from whoever's slab faces the target
function fire(g: Game, s: CubeState, fz: number) {
  if (--s.fireT > 0) return;
  s.fireT = secs(lerp(0.5, 0.28, fz));
  const t = s.target >= 0 ? g.players.get(s.target) : undefined;
  if (!t) return;
  for (let k = 0; k < UNITS; k++) {
    const i = (s.fireRR + k) % UNITS;
    const e = s.units[i];
    if (!e || e.st !== EState.Chase || s.roll[i] > 0 || isDropper(s, i)) continue;
    const front = s.form === Fm.Encircle || s.form === Fm.Gauntlet || wallOf(i) === s.front;
    if (!front) continue;
    if (shoot(g, e, t)) { s.fireRR = (i + 1) % UNITS; return; }
  }
}

function shoot(g: Game, e: Enemy, t: PlayerState) {
  const fx = -Math.sin(e.yaw), fz = -Math.cos(e.yaw);
  const dx = t.x - e.x, dz = t.z - e.z, d = len2(dx, dz);
  if (d > 26 || d < 1.5 || (dx * fx + dz * fz) / d < 0.4) return false;
  cubeRot(e.yaw + Math.PI, MUZZLE[0], MUZZLE[2], o2);
  const ox = e.x + o2.x, oy = e.y + MUZZLE[1], oz = e.z + o2.z;
  const ax = t.x - ox, ay = t.y + 1.1 - oy, az = t.z - oz, l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
  const sp = BULLETS[BKind.Pellet].speed;
  g.fireBullet(BKind.Pellet, ox, oy, oz, (ax / l) * sp, (ay / l) * sp, (az / l) * sp, e.id);
  return true;
}

// ---- slab bash: walk into a wall of oven doors and it shoves back (contact: knockback first, damage ≤ 10)
function bash(g: Game, s: CubeState) {
  for (const p of targets(g)) {
    const gate = p.id & 31;
    if (g.tick - s.bashAt[gate] < BASH_GATE) continue;
    for (const e of s.units) {
      if (!e || e.st === EState.Recover || e.st === EState.Spawn) continue;
      if (Math.abs(p.y - e.y) > 1.8) continue;
      const fx = -Math.sin(e.yaw), fz = -Math.cos(e.yaw);
      const dx = p.x - e.x, dz = p.z - e.z;
      const ahead = dx * fx + dz * fz, side = Math.abs(dx * fz - dz * fx);
      if (ahead < 0.3 || ahead > 1.5 || side > 0.85) continue;
      s.bashAt[gate] = g.tick;
      g.hurtPlayer(p, BASH_DMG, e.x, e.z, 0, fx * 13, fz * 13);
      break;
    }
  }
}

// ---- the broken cube: survivors fight on their own, still owing their rests
function stepReleased(g: Game, s: CubeState) {
  for (let i = 0; i < UNITS; i++) {
    const e = s.units[i];
    if (!e) continue;
    if (s.roll[i] > 0) { rollUnit(g, s, e, i); continue; }
    if (e.burnT > 0) { startRoll(s, e, i); continue; }
    const gy = groundAt(g, e.x, e.z, e.y);
    e.y = e.y > gy ? Math.max(gy, e.y - 9 * DT) : gy;
    if (e.stunT > 0) { e.stunT--; setUnit(e, EState.Stun, e.stunT); continue; }
    // pick the nearest cowboy
    let t: PlayerState | undefined, bd = 1e9;
    for (const p of targets(g)) { const d = len2(p.x - e.x, p.z - e.z); if (d < bd) { bd = d; t = p; } }
    if (--s.rest[i] <= 0) {
      // active 5 s, planted 1.5 s
      const resting = e.st === EState.Recover;
      s.rest[i] = secs(resting ? 5 : 1.5);
      setUnit(e, resting ? EState.Chase : EState.Recover, s.rest[i]);
    }
    if (e.st === EState.Recover || !t) continue;
    setUnit(e, EState.Chase, 0);
    faceTo(e, t.x, t.z, TURN);
    if (bd > 7) walkTo(g, e, t.x, t.z, 3.0);
    else if (bd < 4) walkTo(g, e, e.x - (t.x - e.x), e.z - (t.z - e.z), 2.0);
    if (s.shotT[i] > 0) s.shotT[i]--;
    if (s.shotT[i] === 0 && s.fireT <= 0) {
      if (shoot(g, e, t)) { s.shotT[i] = secs(2.2); s.fireT = secs(0.3); }
    }
  }
  if (s.fireT > 0) s.fireT--;
}
